import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { ResizeDirection, TemplateState } from '../../../../types/common/types';
import { LicensingViewData } from '../types/licensing-view-data';
import { User } from '../../../types/user';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { CompanyData, CompanySpecifics } from '../../../types/company-data';
import { GzTitleWebComponent } from '../../../../ui/gzTitle';
import { GzButtonWebComponent } from '../../../../ui/gzButton';
import { GzInputWebComponent } from '../../../../ui/gzInput/component/GzInputWebComponent';
import { ChangePartnerMenuWebComponent } from '../../../myCompany/changePartnerMenu';
import { GzSideMenuWebComponent } from '../../../../ui/gzSideMenu';
import { SideMenuWidthValues } from '../../../../ui/gzSideMenu/types';
import {
  WEB_COMPONENT_EDIT_COMPANY_PANEL,
  WEB_COMPONENT_GRID_VIEW,
  WEB_COMPONENT_GZ_DIALOG,
  WEB_COMPONENT_GZ_INFO_BOX,
  WEB_COMPONENT_MY_COMPANY_PANEL,
} from '../../../../webcomponents.constants';
import { GzDialogWebComponent } from '../../../../ui/gzDialog';
import { GzCopyToClipboardWebComponent } from '../../../../ui/gzCopyToClipboard';
import { PaymentPlanType } from '../../../types/PaymentPlanType';
import { GridViewWebComponent } from '../../../../grid/gridView';
import { LicenseUsageDetailsDataProvider } from '../../licensingGrid/dataProvider/LicenseUsageDetailsDataProvider';
import {
  ColumnDetails,
  ColumnIndexes,
  ColumnKeys,
  getColumnDetails,
  PAGE_SIZE,
} from '../../licensingGrid/types/licensing-grid-columns';
import {
  getAutoRenewalRenderer,
  getTotalSeatsRenderer,
  getUsageBreakdownRenderer,
  renderExpiryDate,
  renderProductName,
  renderProductStatus,
  renderProductType,
  renderProtectionModel,
  renderSubscriptionEndDate,
} from '../../licensingGrid/view/customGridRenderers';
import { isHidden, toggleVisibility } from '../../../../utils/htmlElement.utils';
import { AddProductMenuWebComponent } from '../../addProductMenu';
import { LicenseUsageDetailsResponse } from '../../licensingGrid/types/licensing-grid-responses';
import { ActionEnum } from '../../addProductMenu/types/ActionEnum';
import { BehaviorSubject, Subscription, timer } from 'rxjs';
import { LicensingConstants } from '../LicensingConstants';
import { GzTooltipWebComponent } from '../../../../ui/gzTooltip';
import { RefreshTooltipCase } from '../LicensingEnums';
import { CompanyActionContext } from '../../types/CompanyActionContext';
import { GzToastNotificationWebComponent } from '../../../../ui/gzToastNotification';
import { ToastSeverity } from '../../../../ui/gzToastNotification/type';
import { CompanyLicenseInformation } from '../../../../license/types/CompanyLicenseInformation';
import { EditLicensingOptionsMenuWebComponent } from '../../editLicensingOptionsMenu';
import { CompanyLicensingTabHelper } from './CompanyLicensingTabHelper';
import { CompanyEditingRight } from '../../types/CompanyEditingRight';
import { GzInfoBoxWebComponent } from '../../../../ui/gzInfoBox';
import { InfoBoxSeverity } from '../../../../ui/gzInfoBox/types';
import { getTranslatedPaymentPlan } from '../../utils/PaymentPlan';
import _ from 'lodash';
import { AddProductViewData } from '../../addProductMenu/types/types';
import { AwsSubscriptionType } from '../../../../license/types/AwsSubscriptionType';
import { GzIzIntegrationStatusWebComponent } from '../../gzIzIntegrationStatus';
import { CompanyViewType, GzIzIntegrationStatus } from '../../gzIzIntegrationStatus/types/GzIzIntegrationStatusTypes';
import { GzLabeledContentWebComponent } from '../../../../ui/gzLabeledContent';
import { htmlEncode } from '../../../../utils/escape.utils';
import { getMdrServiceDateString, getMdrServiceFlavorString, getStatusWithBullet } from '../MdrUtils';
import { GzLinkWebComponent } from 'apps/webcomponents/src/index.ui';

export class CompanyLicensingTabView extends BaseWebComponentView {
  public dom: {
    $wrapper: HTMLElement;

    $readonlyNotice: GzInfoBoxWebComponent;

    $parentCompanyDetailsCard: HTMLElement;

    $licenseCardTitle: GzTitleWebComponent;
    $parentCompanyCardTitle: GzTitleWebComponent;

    licenseCard: {
      $companyId: GzFormFieldWebComponent<GzInputWebComponent, string>;
      $copyToClipboardCompanyId: GzCopyToClipboardWebComponent;

      $companyHash: GzFormFieldWebComponent<GzInputWebComponent, string>;
      $copyToClipboardCompanyHash: GzCopyToClipboardWebComponent;

      $paymentPlan: GzFormFieldWebComponent<GzInputWebComponent, string>;

      mdr: {
        $mdrSubscription: GzLabeledContentWebComponent;
        $mdrStartEndDate: GzLabeledContentWebComponent;
        $mdrStatus: GzLabeledContentWebComponent;
      };

      aws: {
        subscriptionContainer: HTMLElement;

        awsLabel: HTMLElement;

        awsMarketplaceLink: GzLinkWebComponent;
        awsMarketplaceLinkImage: HTMLImageElement;

        awsPanLinkImage: HTMLImageElement;
      };

      licensingCardButtons: {
        $editLicensingOptionsButton: GzButtonWebComponent;
        $changeToPrepaidLicenseButton: GzButtonWebComponent;
        $showMonthlyUsageReportButton: GzButtonWebComponent;

        $linkWithIntelliZone: GzIzIntegrationStatusWebComponent;
      };
    };

    partnerCard: {
      form: {
        $id: GzFormFieldWebComponent<GzInputWebComponent, string>;
        $name: GzFormFieldWebComponent<GzInputWebComponent, string>;
        $address: GzFormFieldWebComponent<GzInputWebComponent, string>;
        $phoneNumber: GzFormFieldWebComponent<GzInputWebComponent, string>;
      };

      $showChangePartnerMenuButton: GzButtonWebComponent;
    };

    licenseUsageDetails: {
      $title: GzTitleWebComponent;
      $gridView: GridViewWebComponent<ColumnIndexes, ColumnKeys>;
      $addProductButton: GzButtonWebComponent;
      $deleteProductButton: GzButtonWebComponent;
      $buyProductButton: GzButtonWebComponent;
      $refreshProductButton: GzButtonWebComponent;
      $refreshProductButtonTooltip: GzTooltipWebComponent;
      gridViewInfo: {
        $gridViewInfoBoxMessage: HTMLSpanElement;
        $gridViewEmptyStateText: HTMLSpanElement;
      };
      logoutNotice: {
        $logoutWarningIcon: HTMLElement;
        $logoutInfoText: HTMLElement;
        $logoutButton: GzButtonWebComponent;
      };
    };
  };

  public company: CompanyData;
  public companyLicenseInfo: CompanyLicenseInformation | null;

  public usageDetails: LicenseUsageDetailsResponse;

  public sideMenuParent: HTMLElement;
  public sideMenuParentDialog: GzDialogWebComponent;

  public $changePartnerSideMenu: GzSideMenuWebComponent;
  public $changePartner: ChangePartnerMenuWebComponent;

  public $addProductSideMenu: GzSideMenuWebComponent;
  public $addProduct: AddProductMenuWebComponent;

  public $changeMspToYearlyLicensingSideMenu: GzSideMenuWebComponent;
  public $changeMspToYearlyLicensing: AddProductMenuWebComponent;

  public $editLicensingOptionsSideMenu: GzSideMenuWebComponent;
  public $editLicensingOptionsComponent: EditLicensingOptionsMenuWebComponent;

  public licenseMainFormValidSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  private licenseMainFormValidSubscription?: Subscription;

  private timerSubscription: Subscription | null = null;
  private refreshProductButtonTooltipText;

  private context: CompanyActionContext;
  private loggedUser: User;

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('.wrapper'),

      $readonlyNotice: this.shadow.querySelector(WEB_COMPONENT_GZ_INFO_BOX),

      $parentCompanyDetailsCard: this.shadow.querySelector('#parent-company-details-card'),

      $licenseCardTitle: this.shadow.querySelector('#license-card-title'),
      $parentCompanyCardTitle: this.shadow.querySelector('#parent-company-card-title'),

      licenseCard: {
        $companyId: this.shadow.querySelector('#company-id'),
        $copyToClipboardCompanyId: this.shadow.querySelector('#copyToClipboardCompanyId'),

        $companyHash: this.shadow.querySelector('#company-hash'),
        $copyToClipboardCompanyHash: this.shadow.querySelector('#copyToClipboardCompanyHash'),

        $paymentPlan: this.shadow.querySelector('#payment-plan'),

        mdr: {
          $mdrSubscription: this.shadow.querySelector('#mdr-subscription-type'),
          $mdrStartEndDate: this.shadow.querySelector('#mdr-start-end-date'),
          $mdrStatus: this.shadow.querySelector('#mdr-status'),
        },

        aws: {
          subscriptionContainer: this.shadow.querySelector('#aws-subscription-container'),

          awsLabel: this.shadow.querySelector('#aws-subscription-label'),

          awsMarketplaceLink: this.shadow.querySelector('#aws-marketplace-link'),
          awsMarketplaceLinkImage: this.shadow.querySelector('#aws-marketplace-link-image'),

          awsPanLinkImage: this.shadow.querySelector('#aws-pan-link-image'),
        },
        licensingCardButtons: {
          $changeToPrepaidLicenseButton: this.shadow.querySelector('#change-to-pre-paid-license'),
          $editLicensingOptionsButton: this.shadow.querySelector('#edit-licensing-options'),
          $showMonthlyUsageReportButton: this.shadow.querySelector('#show-monthly-usage-report-menu-button'),

          $linkWithIntelliZone: this.shadow.querySelector('gz-iz-integration-status'),
        },
      },

      partnerCard: {
        form: {
          $id: this.shadow.querySelector('#partner-id'),
          $name: this.shadow.querySelector('#partner-name'),
          $address: this.shadow.querySelector('#partner-address'),
          $phoneNumber: this.shadow.querySelector('#partner-phone-number'),
        },

        $showChangePartnerMenuButton: this.shadow.querySelector('#show-change-partner-menu-button'),
      },

      licenseUsageDetails: {
        $title: this.shadow.querySelector('#license-usage-details-title'),
        $gridView: this.shadow.querySelector(WEB_COMPONENT_GRID_VIEW) as GridViewWebComponent<
          ColumnIndexes,
          ColumnKeys
        >,
        $addProductButton: this.shadow.querySelector('#add-product-button'),
        $deleteProductButton: this.shadow.querySelector('#delete-product-button'),
        $buyProductButton: this.shadow.querySelector('#buy-product-button'),
        $refreshProductButton: this.shadow.querySelector('#refresh-product-button'),
        $refreshProductButtonTooltip: this.shadow.querySelector('#refresh-button-tooltip'),

        gridViewInfo: {
          $gridViewInfoBoxMessage: this.shadow.querySelector('#license-usage-details-info-box-message'),
          $gridViewEmptyStateText: this.shadow.querySelector('#license-usage-details-empty-state-text'),
        },

        logoutNotice: {
          $logoutWarningIcon: this.shadow.querySelector('#logout-warning-icon'),
          $logoutInfoText: this.shadow.querySelector('#logout-info-text'),
          $logoutButton: this.shadow.querySelector('#logout-button'),
        },
      },
    };

    this.setSideMenuParent();
    this.setLoadingState();

    this.getTranslations();
    this.setTranslatedStrings();
  }

  getTranslations() {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
  }

  setTranslatedStrings() {
    this.dom.$readonlyNotice.severity = InfoBoxSeverity.INFORMATIVE;
    this.dom.$readonlyNotice.innerHTML = this.commonStrings.readOnlyNotice;

    this.dom.licenseUsageDetails.$title.innerText = this.dependencies.translateFn('License usage details');
    this.dom.$licenseCardTitle.innerText = this.dependencies.translateFn('License information');
    this.dom.licenseCard.$copyToClipboardCompanyId.confirmationMessage = this.commonStrings.copied;
    this.dom.licenseCard.$copyToClipboardCompanyHash.confirmationMessage = this.commonStrings.copied;
    this.dom.licenseCard.$paymentPlan.label = this.dependencies.translateFn('Payment plan');

    this.dom.licenseCard.mdr.$mdrSubscription.label = this.dependencies.translateFn('{MDR_SERVICE} service model', {
      MDR_SERVICE: 'MDR',
    });
    this.dom.licenseCard.mdr.$mdrStartEndDate.label = this.dependencies.translateFn(
      '{MDR_SERVICE} entitlement period',
      { MDR_SERVICE: 'MDR' }
    );
    this.dom.licenseCard.mdr.$mdrStatus.label = this.dependencies.translateFn('{MDR_SERVICE} status', {
      MDR_SERVICE: 'MDR',
    });

    this.dom.licenseCard.aws.awsLabel.innerText = this.dependencies.translateFn('AWS subscription type');
    this.dom.licenseCard.aws.awsMarketplaceLinkImage.alt = this.dependencies.translateFn('AWS Marketplace logo');
    this.dom.licenseCard.aws.awsPanLinkImage.alt = this.dependencies.translateFn('AWS PAN logo');

    this.dom.licenseCard.licensingCardButtons.$changeToPrepaidLicenseButton.innerText =
      this.dependencies.translateFn('Change to pre-paid license');
    this.dom.licenseCard.licensingCardButtons.$editLicensingOptionsButton.innerText =
      this.dependencies.translateFn('Edit licensing options');
    this.dom.licenseCard.licensingCardButtons.$showMonthlyUsageReportButton.innerText =
      this.dependencies.translateFn('View monthly usage report');

    this.dom.$parentCompanyCardTitle.innerText = this.dependencies.translateFn('Bitdefender partner');

    this.dom.partnerCard.form.$name.label = this.dependencies.translateFn('Company name');
    this.dom.partnerCard.form.$id.label = this.dependencies.translateFn('Company hash');
    this.dom.partnerCard.form.$address.label = this.dependencies.translateFn('Address');
    this.dom.partnerCard.form.$phoneNumber.label = this.dependencies.translateFn('Phone');

    this.dom.partnerCard.$showChangePartnerMenuButton.innerText = this.dependencies.translateFn('Change partner');

    this.dom.licenseUsageDetails.$addProductButton.innerText = this.dependencies.translateFn('Add product');
    this.dom.licenseUsageDetails.$deleteProductButton.innerText = this.dependencies.translateFn('Remove product');
    this.dom.licenseUsageDetails.$buyProductButton.innerText = this.dependencies.translateFn('Buy product');

    this.dom.licenseUsageDetails.$refreshProductButton.innerText = this.dependencies.translateFn('Refresh details');
    this.refreshProductButtonTooltipText = {
      [RefreshTooltipCase.RecentlyUsed]: this.dependencies.translateFn(
        'Product details have just been refreshed. Please wait about 15 minutes until you can refresh them again.'
      ),
      [RefreshTooltipCase.NoTooltip]: '',
    };

    this.dom.licenseUsageDetails.logoutNotice.$logoutInfoText.innerText = this.dependencies.translateFn(
      'To apply the changes to the products across the company, all users must be logged out. They can view the changes after they log back in to GravityZone.'
    );
    this.dom.licenseUsageDetails.logoutNotice.$logoutButton.innerText =
      this.dependencies.translateFn('Log out all users');

    this.dom.licenseUsageDetails.gridViewInfo.$gridViewEmptyStateText.innerText =
      this.dependencies.translateFn('Try adding a new product');
  }

  public setSideMenuParent() {
    if (this.sideMenuParentDialog) {
      // if Edit Company is opened from Companies, this will be already set through the component
      this.sideMenuParent = this.sideMenuParentDialog?.shadowRoot?.querySelector('.window') || this.dom.$wrapper;
      return;
    }

    const windowDialogs: NodeListOf<GzDialogWebComponent> = document.body.querySelectorAll(
      WEB_COMPONENT_GZ_DIALOG + '[isWindow]'
    ) as NodeListOf<GzDialogWebComponent>;

    // If there are multiple dialogs in dom, we must select the one for the company (edit or my company)
    for (let i = 0; i < windowDialogs.length; i++) {
      const dialog = windowDialogs[i];

      if (
        dialog?.shadowRoot?.querySelector(WEB_COMPONENT_MY_COMPANY_PANEL) ||
        dialog?.shadowRoot?.querySelector(WEB_COMPONENT_EDIT_COMPANY_PANEL)
      ) {
        this.sideMenuParent = dialog?.shadowRoot?.querySelector('.window') || this.dom.$wrapper;
        return;
      }
    }

    this.sideMenuParent = this.dom.$wrapper;
  }

  public setLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
  }

  public removeLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
  }

  public async updateCompanyInformation(
    company: CompanyData,
    companyLicenseInfo: CompanyLicenseInformation | null
  ): Promise<void> {
    this.company = company;
    this.companyLicenseInfo = companyLicenseInfo;

    await this.buildLicenseUsageDetails(company);
    await this.buildLicenseInformationCard(company);
    await this.setupLeftGridHeaderActions(company, companyLicenseInfo);
    this.updateEditLicensingOptions();

    this.setLicensingTabToDisabled(company);
  }

  public async updateParentCompanyInformation(company: CompanyData, loggedUser: User): Promise<void> {
    this.company = company;
    this.loggedUser = loggedUser;

    await this.buildLicenseInformationCard(company);
    this.buildParentCompanyCard(company, loggedUser);

    this.setLicensingTabToDisabled(company);
  }

  public updateEditLicensingOptions(): void {
    if (this.context !== CompanyActionContext.Edit || !this.$editLicensingOptionsComponent) {
      return;
    }

    this.$editLicensingOptionsComponent.propertiesSet = false;
    this.$editLicensingOptionsComponent.company = this.company;
    this.$editLicensingOptionsComponent.companyLicenseInformation = this.companyLicenseInfo;
    this.$editLicensingOptionsComponent.propertiesSet = true;

    this.toggleEditLicensingOptionsButtonVisibility(this.companyLicenseInfo);

    this.$editLicensingOptionsComponent.resetPrepaidSubform();
  }

  public changeLogoutNoticeVisibility(shouldTextBeVisible: boolean, force: boolean = false) {
    const isTextAlreadyVisible =
      !isHidden(this.dom.licenseUsageDetails.logoutNotice.$logoutWarningIcon) &&
      !isHidden(this.dom.licenseUsageDetails.logoutNotice.$logoutInfoText);

    // if text is already visible and force is false, do not change the text visibility
    // only logout button visibility needs to be changed because of manageUsers right in Edit context
    if (!force && isTextAlreadyVisible) {
      toggleVisibility(
        this.dom.licenseUsageDetails.logoutNotice.$logoutButton,
        CompanyLicensingTabHelper.shouldLogoutButtonBeVisible(this.loggedUser, this.context, true)
      );
      return;
    }

    toggleVisibility(this.dom.licenseUsageDetails.logoutNotice.$logoutWarningIcon, shouldTextBeVisible);
    toggleVisibility(this.dom.licenseUsageDetails.logoutNotice.$logoutInfoText, shouldTextBeVisible);
    toggleVisibility(
      this.dom.licenseUsageDetails.logoutNotice.$logoutButton,
      CompanyLicensingTabHelper.shouldLogoutButtonBeVisible(this.loggedUser, this.context, shouldTextBeVisible)
    );
  }

  buildSuccessView({ company, loggedUser, companyLicenseInfo, context }: LicensingViewData) {
    this.company = company;
    this.companyLicenseInfo = companyLicenseInfo;
    this.context = context;
    this.loggedUser = loggedUser;

    this.dom.licenseUsageDetails.$gridView.injectDependencies(this.dependencies);
    this.dom.licenseUsageDetails.$gridView.start().then().catch(console.error);

    this.buildCompanyLicensingView({ company, loggedUser, companyLicenseInfo, context })
      .then(() => {
        if (this.$editLicensingOptionsComponent) {
          this.$editLicensingOptionsComponent.setCompanyType(CompanyData.getCompanyType(company));
        }

        this.removeLoadingState();
      })
      .catch(console.error);
  }

  buildErrorView(error: Error) {
    this.removeLoadingState();
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: error.message,
      severity: ToastSeverity.ERROR,
    });
    toggleVisibility(this.dom.$wrapper, false);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  protected onDisconnect() {
    this.timerSubscription?.unsubscribe();
  }

  public isCompanyReadOnly(company: CompanyData) {
    const editingRight = CompanyEditingRight.getCompanyEditingRight(
      company.readOnly ?? false,
      company.specifics.canBeManagedByAbove,
      this.context
    );

    return editingRight === CompanyEditingRight.None;
  }

  private async buildCompanyLicensingView({ company, loggedUser, companyLicenseInfo, context }: LicensingViewData) {
    this.setCompanyIdTranslatedLabelFromContext();
    this.setCompanyHashTranslatedLabelFromContext();

    await this.buildLicenseInformationCard(company);
    this.buildParentCompanyCard(company, loggedUser);
    await this.buildLicenseUsageDetails(company);

    await this.buildChangePartnerSideMenu();
    await this.setupLeftGridHeaderActions(company, companyLicenseInfo);

    await this.setupChangeMspToYearlyMenu(company);
    await this.setupEditLicenseOptionsMenu(companyLicenseInfo);

    this.$changePartner.setCompany(company);

    this.setLicensingTabToDisabled(company);
  }

  private async buildLicenseInformationCard(company: CompanyData) {
    this.dom.licenseCard.$companyId.value = company._id;
    this.dom.licenseCard.$companyHash.value = company.specifics.hash;
    this.dom.licenseCard.$paymentPlan.value = getTranslatedPaymentPlan(company.specifics.paymentPlan);

    if (CompanyData.shouldDisplayMdrStatus(company)) {
      this.buildMdrSection(company.specifics);
    } else {
      this.hideMdrSection();
    }

    if (company.specifics.features.awsEc2IntegrationConfigured) {
      this.buildAwsMarketplaceFields();
    } else {
      this.hideAwsMarketPlaceFields();
    }

    toggleVisibility(
      this.dom.licenseCard.licensingCardButtons.$changeToPrepaidLicenseButton,
      this.shouldChangeMspToPrepaidButtonBeVisible(company.specifics)
    );

    toggleVisibility(
      this.dom.licenseCard.licensingCardButtons.$showMonthlyUsageReportButton,
      company.specifics.paymentPlan === PaymentPlanType.MONTHLY
    );

    await this.handleIntelliZoneStatus(company);
  }

  private setUnlicensedEndpointsMessage(unlicensedEndpointCount: number): void {
    this.dom.licenseUsageDetails.gridViewInfo.$gridViewInfoBoxMessage.innerHTML = this.dependencies.translateFn(
      'You have <b>{count}</b> unlicensed {endpointText}!',
      {
        count: unlicensedEndpointCount,
        endpointText:
          unlicensedEndpointCount === 1
            ? this.dependencies.translateFn('endpoint')
            : this.dependencies.translateFn('endpoints'),
      }
    );
    this.dom.licenseUsageDetails.gridViewInfo.$gridViewInfoBoxMessage.appendChild(document.createElement('br'));
    const message = this.dependencies.translateFn(
      'To find these endpoints, generate a Network Protection Status report and apply the EPP filter on the following columns, one at a time: Licenses expired, Trial licenses and Pending licenses.'
    );
    this.dom.licenseUsageDetails.gridViewInfo.$gridViewInfoBoxMessage.appendChild(document.createTextNode(message));
  }

  private async setupLeftGridHeaderActions(company: CompanyData, companyLicenseInfo: CompanyLicenseInformation | null) {
    if (CompanyLicensingTabHelper.shouldAddActionBeVisible(company, companyLicenseInfo, this.context)) {
      await this.setupAddProductHeaderAction(company);
    } else {
      toggleVisibility(this.dom.licenseUsageDetails.$addProductButton, false);
    }

    if (CompanyLicensingTabHelper.shouldDeleteActionBeVisible(company, companyLicenseInfo, this.context)) {
      this.setupDeleteProductHeaderAction();
    } else {
      toggleVisibility(this.dom.licenseUsageDetails.$deleteProductButton, false);
    }

    if (CompanyLicensingTabHelper.shouldBuyProductButtonBeVisible(this.context)) {
      this.setupBuyProductHeaderAction();
    } else {
      toggleVisibility(this.dom.licenseUsageDetails.$buyProductButton, false);
    }

    if (CompanyLicensingTabHelper.shouldRefreshActionBeVisible(company)) {
      this.setupRefreshProductHeaderAction();
    } else {
      toggleVisibility(this.dom.licenseUsageDetails.$refreshProductButton, false);
    }
  }

  private async setupAddProductHeaderAction(company: CompanyData) {
    await this.buildAddProductSideMenu();

    this.$addProduct.setViewData({
      context: this.context,
      company: company,
      action: ActionEnum.addProduct,
      licenseUsageDetails: this.usageDetails,
    });

    toggleVisibility(this.dom.licenseUsageDetails.$addProductButton, true);
  }

  private async setupChangeMspToYearlyMenu(company: CompanyData) {
    if (this.context !== CompanyActionContext.MyCompany) {
      return;
    }

    await this.buildChangeMspToYearlySideMenu();

    this.$changeMspToYearlyLicensing.setViewData({
      context: this.context,
      company: company,
      action: ActionEnum.changeProduct,
      licenseUsageDetails: this.usageDetails,
    });
  }

  private async setupEditLicenseOptionsMenu(companyLicenseInfo: CompanyLicenseInformation | null) {
    if (this.context !== CompanyActionContext.Edit) {
      return;
    }
    if (!this.$editLicensingOptionsComponent) {
      await this.buildEditLicensingOptionsSideMenu(companyLicenseInfo);
    } else {
      this.updateEditLicensingOptions();
    }
  }

  private setupDeleteProductHeaderAction() {
    toggleVisibility(this.dom.licenseUsageDetails.$deleteProductButton, true);
  }

  private setupBuyProductHeaderAction() {
    toggleVisibility(this.dom.licenseUsageDetails.$buyProductButton, true);
  }

  private setupRefreshProductHeaderAction() {
    toggleVisibility(this.dom.licenseUsageDetails.$refreshProductButton, true);
    this.setTimerOnRefreshButton();
  }

  private async buildLicenseUsageDetails(company: CompanyData) {
    this.usageDetails = await this.dependencies.api.clients.Licenses.getLicenseUsageDetails(company._id).toPromise();
    const licenseUsageDetailsDataProvider = new LicenseUsageDetailsDataProvider(this.dependencies, this.usageDetails);
    const allColumnDetails = getColumnDetails(this.dependencies.translateFn);
    const visibleColumns = licenseUsageDetailsDataProvider.getVisibleColumns(
      this.usageDetails,
      company.specifics.rights.manageCompanies
    );

    const gridView = this.dom.licenseUsageDetails.$gridView;

    gridView.propertiesSet = false;

    if (this.usageDetails.unlicensedEndpointCount) {
      this.setUnlicensedEndpointsMessage(this.usageDetails.unlicensedEndpointCount);
      toggleVisibility(this.dom.licenseUsageDetails.gridViewInfo.$gridViewInfoBoxMessage, true);
    } else {
      toggleVisibility(this.dom.licenseUsageDetails.gridViewInfo.$gridViewInfoBoxMessage, false);
    }

    gridView.dataProvider = licenseUsageDetailsDataProvider;
    gridView.columnRenderers = {
      [ColumnKeys.PRODUCT_STATUS]: renderProductStatus,
      [ColumnKeys.PRODUCT_TYPE]: renderProductType,
      [ColumnKeys.PROTECTION_MODEL]: renderProtectionModel,
      [ColumnKeys.SUBSCRIPTION_END_DATE]: renderSubscriptionEndDate.bind(this),
      [ColumnKeys.PRODUCT_ID]: renderProductName,
      [ColumnKeys.EXPIRY_DATE]: renderExpiryDate.bind(this),
      [ColumnKeys.AUTO_RENEWAL]: getAutoRenewalRenderer,
      [ColumnKeys.TOTAL_SEATS]: getTotalSeatsRenderer.bind(this),
      [ColumnKeys.USAGE_BREAKDOWN]: getUsageBreakdownRenderer,
    };

    gridView.viewId = '12245';
    gridView.allColumnDetails = allColumnDetails;
    gridView.columns = visibleColumns;
    gridView.isCompactViewChecked = false;
    gridView.pageSize = PAGE_SIZE;
    gridView.sideMenuParent = this.dom.$wrapper;
    gridView.customStrings = {
      noSearchResultsString: this.dependencies.translateFn('No products'),
    };

    gridView.hasSelectableRows = !this.usageDetails.hasMonthly;

    const defaultFlexGrowColumn = allColumnDetails.find((columnDetails: ColumnDetails) => {
      return columnDetails.column === visibleColumns[visibleColumns.length - 1];
    });

    if (defaultFlexGrowColumn) {
      gridView.defaultFlexGrowColumn = defaultFlexGrowColumn.key;
    }

    gridView.columnConfigs = [
      { key: ColumnKeys.PRODUCT_ID, width: '300px' },
      { key: ColumnKeys.PRODUCT_TYPE, width: '150px' },
      { key: ColumnKeys.PROTECTION_MODEL, width: '150px' },
      { key: ColumnKeys.PRODUCT_STATUS, width: '150px' },
      { key: ColumnKeys.LICENSE_KEY, width: '150px' },
      { key: ColumnKeys.EXPIRY_DATE, width: '150px' },
      { key: ColumnKeys.TOTAL_SEATS, width: '130px' },
      { key: ColumnKeys.RESERVED_SEATS, width: '130px' },
      { key: ColumnKeys.USED_RESERVED_SEATS, width: '150px' },
      { key: ColumnKeys.AVAILABLE_RESERVED_SEATS, width: '170px' },
      { key: ColumnKeys.UNRESERVED_SEATS, width: '130px' },
      { key: ColumnKeys.USED_UNRESERVED_SEATS, width: '170px' },
      { key: ColumnKeys.AVAILABLE_UNRESERVED_SEATS, width: '190px' },
      { key: ColumnKeys.MINIMAL_COMMITMENT, width: '150px' },
      { key: ColumnKeys.SUBSCRIPTION_END_DATE, width: '150px' },
      { key: ColumnKeys.AUTO_RENEWAL, width: '150px' },
    ];

    gridView.propertiesSet = true;
  }

  private buildMdrSection(companySpecifics: CompanySpecifics) {
    const mdrProductService = companySpecifics.productServices?.find(service => service.type === 1);

    if (!mdrProductService) {
      this.hideMdrSection();
      return;
    }

    const mdrFlavourText = getMdrServiceFlavorString(mdrProductService.flavour);
    const dateText = getMdrServiceDateString(mdrProductService);
    const { bullet, mdrStatusText } = getStatusWithBullet(mdrProductService?.status);

    this.dom.licenseCard.mdr.$mdrSubscription.innerHTML = `<gz-text fontSize='12' class="content-text">${htmlEncode(
      mdrFlavourText
    )}</gz-text>`;
    this.dom.licenseCard.mdr.$mdrStartEndDate.innerHTML = `<gz-text fontSize='12' class="content-text">${htmlEncode(
      dateText
    )}</gz-text>`;
    this.dom.licenseCard.mdr.$mdrStatus.innerHTML = `<gz-text class='has-bullet ${bullet} content-text' fontSize='12'>${htmlEncode(
      mdrStatusText
    )}</gz-text>`;

    toggleVisibility(this.shadow.querySelector('#mdr-container'), true);
  }

  private hideMdrSection() {
    toggleVisibility(this.shadow.querySelector('#mdr-container'), false);
  }

  private buildAwsMarketplaceFields() {
    const subscriptionType = this.dependencies.AwsController.getSubscriptionTypeForCurrentCompany();

    switch (subscriptionType) {
      case AwsSubscriptionType.None:
      case AwsSubscriptionType.EndUser:
        return;

      case AwsSubscriptionType.Marketplace:
        this.dom.licenseCard.aws.awsMarketplaceLink.setAttribute(
          'href',
          this.dependencies.AwsController.getMarketplaceUrlForCurrentSubscription()
        );
        toggleVisibility(this.dom.licenseCard.aws.awsPanLinkImage, false);
        toggleVisibility(this.dom.licenseCard.aws.awsMarketplaceLink, true);

        break;

      case AwsSubscriptionType.PAN:
        toggleVisibility(this.dom.licenseCard.aws.awsMarketplaceLink, false);
        toggleVisibility(this.dom.licenseCard.aws.awsPanLinkImage, true);

        break;
    }

    toggleVisibility(this.dom.licenseCard.aws.subscriptionContainer, true);
  }

  private hideAwsMarketPlaceFields() {
    toggleVisibility(this.dom.licenseCard.aws.subscriptionContainer, false);
  }

  private buildParentCompanyCard(company: CompanyData, loggedUser: User) {
    if (this.context !== CompanyActionContext.MyCompany || !company.company || !loggedUser.role.rights.companyManager) {
      toggleVisibility(this.dom.$parentCompanyDetailsCard, false);
      return;
    }

    const parentCompany = company.company;

    this.dom.$parentCompanyDetailsCard.classList.remove('hidden');

    this.dom.partnerCard.form.$id.value = parentCompany.hash;
    this.dom.partnerCard.form.$name.value = parentCompany.name;
    this.dom.partnerCard.form.$address.value = parentCompany.address;
    this.dom.partnerCard.form.$phoneNumber.value = parentCompany.phone;
  }

  private async buildChangePartnerSideMenu() {
    if (this.$changePartnerSideMenu) {
      return;
    }

    this.$changePartnerSideMenu = new GzSideMenuWebComponent();
    this.$changePartnerSideMenu.closeOnClickOutside = false;
    this.$changePartnerSideMenu.menuWidth = SideMenuWidthValues.MEDIUM;
    // make sure the sidenav is always the on top
    this.$changePartnerSideMenu.style.zIndex = '9999';

    this.sideMenuParent.appendChild(this.$changePartnerSideMenu);

    this.$changePartner = new ChangePartnerMenuWebComponent();
    this.$changePartnerSideMenu.appendChild(this.$changePartner);

    this.$changePartner.injectDependencies(this.dependencies);
    await this.$changePartner.start();
  }

  private async buildAddProductSideMenu() {
    if (this.$addProductSideMenu) {
      return;
    }

    this.$addProductSideMenu = new GzSideMenuWebComponent();
    this.$addProductSideMenu.closeOnClickOutside = false;
    this.$addProductSideMenu.menuWidth = SideMenuWidthValues.MEDIUM;
    // make sure the sidenav is always the on top
    this.$addProductSideMenu.style.zIndex = '9999';

    this.sideMenuParent.appendChild(this.$addProductSideMenu);

    this.$addProduct = new AddProductMenuWebComponent();
    this.$addProductSideMenu.appendChild(this.$addProduct);

    this.$addProduct.injectDependencies(this.dependencies);
    await this.$addProduct.start();
  }

  private async buildChangeMspToYearlySideMenu() {
    if (this.$changeMspToYearlyLicensingSideMenu) {
      return;
    }

    this.$changeMspToYearlyLicensingSideMenu = new GzSideMenuWebComponent();
    this.$changeMspToYearlyLicensingSideMenu.closeOnClickOutside = false;
    this.$changeMspToYearlyLicensingSideMenu.menuWidth = SideMenuWidthValues.MEDIUM;
    // make sure the sidenav is always the on top
    this.$changeMspToYearlyLicensingSideMenu.style.zIndex = '9999';

    this.sideMenuParent.appendChild(this.$changeMspToYearlyLicensingSideMenu);

    this.$changeMspToYearlyLicensing = new AddProductMenuWebComponent();

    this.$changeMspToYearlyLicensingSideMenu.appendChild(this.$changeMspToYearlyLicensing);

    this.$changeMspToYearlyLicensing.injectDependencies(this.dependencies);
    await this.$changeMspToYearlyLicensing.start().catch(console.error);
  }

  private async buildEditLicensingOptionsSideMenu(companyLicenseInfo: CompanyLicenseInformation | null) {
    if (this.$editLicensingOptionsSideMenu) {
      return;
    }

    this.$editLicensingOptionsSideMenu = new GzSideMenuWebComponent();
    this.$editLicensingOptionsSideMenu.closeOnClickOutside = false;
    this.$editLicensingOptionsSideMenu.menuWidth = SideMenuWidthValues.WIDE;
    // make sure the sidenav is always the on top
    this.$editLicensingOptionsSideMenu.style.zIndex = '9999';
    this.$editLicensingOptionsSideMenu.resizable = ResizeDirection.LEFT;

    this.sideMenuParent.appendChild(this.$editLicensingOptionsSideMenu);

    this.$editLicensingOptionsComponent = new EditLicensingOptionsMenuWebComponent();
    this.$editLicensingOptionsComponent.company = this.company;
    this.$editLicensingOptionsComponent.companyLicenseInformation = companyLicenseInfo;
    this.$editLicensingOptionsComponent.context = this.context;

    this.$editLicensingOptionsSideMenu.appendChild(this.$editLicensingOptionsComponent);

    this.$editLicensingOptionsComponent.injectDependencies(this.dependencies);
    await this.$editLicensingOptionsComponent.start();
    this.toggleEditLicensingOptionsButtonVisibility(companyLicenseInfo);
  }

  private toggleEditLicensingOptionsButtonVisibility(companyLicenseInfo: CompanyLicenseInformation | null) {
    toggleVisibility(
      this.dom.licenseCard.licensingCardButtons.$editLicensingOptionsButton,
      this.shouldEditLicensingOptionsButtonBeVisible(this.company, companyLicenseInfo)
    );
  }

  public changeRefreshButtonAvailability(shouldBeAvailable: boolean) {
    this.dom.licenseUsageDetails.$refreshProductButton.disabled = !shouldBeAvailable;
    this.dom.licenseUsageDetails.$refreshProductButtonTooltip.tooltipText = shouldBeAvailable
      ? this.refreshProductButtonTooltipText[RefreshTooltipCase.NoTooltip]
      : this.refreshProductButtonTooltipText[RefreshTooltipCase.RecentlyUsed];
  }

  private setTimerOnRefreshButton() {
    if (this.isCompanyReadOnly(this.company)) {
      return;
    }

    const timerValue = this.getTimerValue();
    if (!timerValue) {
      this.changeRefreshButtonAvailability(true);
      return;
    }

    const timer$ = timer(timerValue);
    this.changeRefreshButtonAvailability(false);

    this.timerSubscription?.unsubscribe();
    this.timerSubscription = timer$.subscribe(val => {
      // time is up
      this.changeRefreshButtonAvailability(true);
    });
  }

  private setLicensingTabToDisabled(company: CompanyData) {
    if (!this.isCompanyReadOnly(company)) {
      return;
    }

    this.dom.licenseCard.$companyId.disabled = true;
    this.dom.licenseCard.$copyToClipboardCompanyId.disabled = true;
    this.dom.licenseCard.$companyHash.disabled = true;
    this.dom.licenseCard.$copyToClipboardCompanyHash.disabled = true;
    this.dom.licenseCard.$paymentPlan.disabled = true;

    this.dom.licenseUsageDetails.$addProductButton.disabled = true;
    this.dom.licenseUsageDetails.$deleteProductButton.disabled = true;
    this.dom.licenseUsageDetails.$buyProductButton.disabled = true;
    this.dom.licenseUsageDetails.$refreshProductButton.disabled = true;
    this.dom.licenseUsageDetails.$refreshProductButtonTooltip.tooltipText =
      this.refreshProductButtonTooltipText[RefreshTooltipCase.NoTooltip];

    this.dom.licenseCard.licensingCardButtons.$editLicensingOptionsButton.disabled = true;
    this.dom.licenseCard.licensingCardButtons.$showMonthlyUsageReportButton.disabled = true;

    toggleVisibility(this.dom.$readonlyNotice, true);
  }

  private getTimerValue(): number {
    let timerValue = Number.MAX_VALUE;
    const currentTime = new Date().getTime();

    for (const licenseUsageData of this.usageDetails.data) {
      if (!licenseUsageData.lastCheckedTimestamp) {
        continue;
      }
      const checkInterval = currentTime - licenseUsageData.lastCheckedTimestamp * 1000;
      timerValue = Math.min(timerValue, checkInterval);
    }

    // lastChecked is undefined
    if (timerValue === Number.MAX_VALUE) {
      return 0;
    }

    return timerValue < LicensingConstants.MinSecondsBetweenManualLicenseRefresh * 1000
      ? LicensingConstants.MinSecondsBetweenManualLicenseRefresh * 1000 - timerValue
      : 0;
  }

  private shouldChangeMspToPrepaidButtonBeVisible(companySpecifics: CompanySpecifics) {
    if (this.context !== CompanyActionContext.MyCompany) {
      return false;
    }

    if (companySpecifics.paymentPlan !== PaymentPlanType.MONTHLY) {
      return false;
    }

    if (companySpecifics.hasMspTrial) {
      return !companySpecifics.inheritedLicense;
    }

    return true;
  }

  private shouldEditLicensingOptionsButtonBeVisible(
    company: CompanyData,
    companyLicenseInfo: CompanyLicenseInformation | null
  ) {
    // TODO: WIP(update with other cases(if there are any others) when the button should toggle its visibility)

    // this.companyLicenseInfo?.hasMonthlyLicense refers to parent company subscription
    return companyLicenseInfo?.hasMonthlyLicense || CompanyData.hasMonthlyPaymentPlan(company);
  }

  private setCompanyIdTranslatedLabelFromContext() {
    // Setting the right label for section
    this.dom.licenseCard.$companyId.label =
      this.context === CompanyActionContext.MyCompany
        ? this.dependencies.translateFn('My company ID')
        : this.dependencies.translateFn('Company ID');
  }

  private setCompanyHashTranslatedLabelFromContext() {
    // Setting the right label for section
    this.dom.licenseCard.$companyHash.label =
      this.context === CompanyActionContext.MyCompany
        ? this.dependencies.translateFn('My company hash')
        : this.dependencies.translateFn('Company hash');
  }

  public discardFormChanges() {
    // do nothing for now
  }

  public updateGeneralAddProductViewData(canBeManagedByAbove: boolean) {
    this.updateAddProductViewData(canBeManagedByAbove);
    this.updateChangeMspToYearlyLicensingViewData(canBeManagedByAbove);
  }

  private updateAddProductViewData(canBeManagedByAbove: boolean) {
    const viewData = this.$addProduct?.getViewData() as AddProductViewData;

    if (!viewData) {
      return;
    }

    const company = viewData.company as CompanyData;
    _.set(company, ['specifics', 'canBeManagedByAbove'], canBeManagedByAbove);

    this.$addProduct.setViewData({
      context: this.context,
      company: company,
      action: ActionEnum.addProduct,
      licenseUsageDetails: this.usageDetails,
    });
  }

  private updateChangeMspToYearlyLicensingViewData(canBeManagedByAbove: boolean) {
    const viewData = this.$changeMspToYearlyLicensing?.getViewData() as AddProductViewData;

    if (!viewData) {
      return;
    }

    const company = viewData.company as CompanyData;
    _.set(company, ['specifics', 'canBeManagedByAbove'], canBeManagedByAbove);

    this.$changeMspToYearlyLicensing.setViewData({
      context: this.context,
      company: company,
      action: ActionEnum.addProduct,
      licenseUsageDetails: this.usageDetails,
    });
  }

  private async handleIntelliZoneStatus(company: CompanyData) {
    // not available for MSP
    if (CompanyData.hasMonthlyLicense(company)) {
      toggleVisibility(this.dom.licenseCard.licensingCardButtons.$linkWithIntelliZone, false);
      return;
    }

    const isCurrentCompanyUsersCompany = Application.Main.getUser().company.id === company._id;
    this.dom.licenseCard.licensingCardButtons.$linkWithIntelliZone.companyViewType = isCurrentCompanyUsersCompany
      ? CompanyViewType.MY_COMPANY
      : CompanyViewType.EDIT_COMPANY;

    if (company.specifics.isLinkedWithIntellizone === true) {
      this.dom.licenseCard.licensingCardButtons.$linkWithIntelliZone.integrationStatus = GzIzIntegrationStatus.LINKED;
      return;
    }
    this.dom.licenseCard.licensingCardButtons.$linkWithIntelliZone.integrationStatus = GzIzIntegrationStatus.UNLINKED;

    // No need for call for access to IPT if it's edit company
    if (!isCurrentCompanyUsersCompany) {
      return;
    }

    // My Company flow should allow pivoting to In Product Trials if available
    this.dom.licenseCard.licensingCardButtons.$linkWithIntelliZone.canPivotToInProductTrials =
      await this.dependencies.api.clients.Company.HasAccessToInProductTrialsIntelliZone(company._id).toPromise();
  }
}
